import styled from '@emotion/styled';
import { Text } from 'ds/components/typography';
import { HEADER_HEIGHT } from 'components/navigation/header.constants';
import { typographyFontSizeCfg, typographyLineHeightCfg } from 'ds/components/typography/types';
import { BreakpointValue } from 'consts/breakpoints';
import { SpacingScale } from 'ds/theme/spacing';
import { isNil } from 'lodash';


export const LISTING_PAGE_CONTAINER_WIDTH = 1440;
const LEFT_SIDE_COLUMN = 360;

function getSidePadding(br: BreakpointValue) {
  switch (br) {
    case 1:
      return 24;
    case 2:
      return 40;
    case 3:
    case 4:
    case 5:
      return 60;
  }
}

export const ListingPageContainer = styled.div<{ topPaddingSpacing?: SpacingScale; }>(({ theme, topPaddingSpacing = null }) => ({
  width: '100%',
  maxWidth: LISTING_PAGE_CONTAINER_WIDTH,
  padding: `${theme.spacing(isNil(topPaddingSpacing) ? 4 : topPaddingSpacing)} ${getSidePadding(3)}px 0`,
  margin: '0 auto',
  [theme.media.createObjectMediaQuery({ at: 2 })]: {
    padding: `${theme.spacing(isNil(topPaddingSpacing) ? 4 : topPaddingSpacing)} ${getSidePadding(2)}px 0`,
  },
  [theme.media.createObjectMediaQuery({ at: 1 })]: {
    padding: `${theme.spacing(isNil(topPaddingSpacing) ? 4 : topPaddingSpacing)} ${getSidePadding(1)}px 0`,
  },
  '.static-blog': {
    backgroundColor: 'transparent',
  },
}));

export const ListingInfoWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  marginTop: theme.spacing(4),
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    marginTop: theme.spacing(3),
  },
}));

export const DisabledEventsWrapper = styled.div`
  pointer-events: none;
`;

export const InsightsHeader = styled.h2(({ theme }) => ({
  display: 'flex',
  color: theme.colors.neutrals.grey1,
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 700,
  fontSize: `${typographyFontSizeCfg.h2.mobile}px`,
  lineHeight: `${typographyLineHeightCfg.h2.mobile}px`,
  padding: theme.spacing(3, 4, 3, 3),
  'svg': {
    display: 'block',
  },
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: `${typographyFontSizeCfg.h3.mobile}px`,
    lineHeight: `${typographyLineHeightCfg.h3.mobile}px`,
  },
}));

export const ContactInnerWrap = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  gap: theme.spacing(3),
  margin: theme.spacing(3, 0, 0),
  '> div:nth-of-type(1)': {
    width: '100%',
  },
  [theme.media.createObjectMediaQuery({ at: 1 })]: {
    gap: theme.spacing(1  ),
  },
}));

export const InsightsMobileWrapper = styled.div(({ theme }) => ({
  maxWidth: LISTING_PAGE_CONTAINER_WIDTH,
  backgroundColor: theme.colors.neutrals.grey9,
  padding: `0 ${getSidePadding(3)}px 0`,
  margin: `${theme.spacing(3)} auto 0`,
  [theme.media.createObjectMediaQuery({ at: 2 })]: {
    padding: `0 ${getSidePadding(2)}px ${theme.spacing(1)}`,
  },
  [theme.media.createObjectMediaQuery({ at: 1 })]: {
    padding: `0 ${getSidePadding(1)}px ${theme.spacing(1)}`,
  },
  '.analyst-insight-container span': {
    backgroundColor: 'transparent!important',
  },
}));

export const ListingInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

export const StickyInfo = styled.div`
  display: flex;
  flex-basis: 360px;
  flex-shrink: 0;
  align-items: flex-start;
  && > div {
    width: 100%;
  }
`;

export const SectionDivider = styled.div(({ theme }) => ({
  height: 1,
  width: '100%',
  backgroundColor: theme.colors.neutrals.grey8,
  margin: theme.spacing(4, 0),
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    margin: theme.spacing(3, 0),
  },
}));


export const TabuWidgetWrapper = styled.div`
  ${({ theme }) => `
    margin-top: ${theme.spacing(4)};
    margin-bottom: ${theme.spacing(4)};
  `}
`;

export const BlogWrapper = styled.div(({ theme }) => ({
  [theme.media.createObjectMediaQuery({ from: 3 })]: {
    '> div > div:nth-of-type(1)': {
      maxWidth: '100%',
      '> div': {
        padding: 0,
      },
    },
    'div.view-pager-rail': {
      paddingRight: 0,
    },
  },
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    '.static-blog > div': {
      '> div:nth-of-type(1)': {
        padding: 0,
      },
      padding: 0,
      margin: 0,
    },
  },
}));

export const BorderlessWidgetContainer = styled.div(({ theme }) => ({
  margin: theme.spacing(0, 4, 2, 3),
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    margin: 0,
  },
}));

export const WidgetContainer = styled.div(({ theme }) => ({
  marginRight: theme.spacing(4),
  marginLeft: theme.spacing(3),
  'article:not(:empty)': {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.colors.neutrals.white,
    border: `solid 1px ${theme.colors.neutrals.grey8}`,
    borderRadius: theme.borderRadius.small,
  },
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    margin: 0,
  },
}));

export const MapExpandToggle = styled.button(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(2),
  top: theme.spacing(2),
  backgroundColor: theme.colors.neutrals.white,
  borderRadius: theme.borderRadius.round,
  width: theme.spacing(5),
  height: theme.spacing(5),
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.shadow.level11,
  cursor: 'pointer',
}));

export const MapSlot = styled.div`
  height: calc(100vh - ${HEADER_HEIGHT}px);
  flex-basis: calc((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - ${getSidePadding(3) * 2}px)) / 2 + ${LEFT_SIDE_COLUMN}px + 32px);
  position: sticky;
  top: ${HEADER_HEIGHT}px;
  flex-shrink: 0;
  &&& .mapboxgl-ctrl-bottom-right {
    right: 0;
  }
  .map-satellite-toggle {
    bottom: 110px;
  }
  &&& .mapboxgl-ctrl-bottom-right {
    bottom: 70px;
  }
`;

export const InsightsContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding-right: calc((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - ${getSidePadding(3) * 2}px)) / 2 - 32px);
  background-color: ${({ theme }) => theme.colors.neutrals.grey9};

  .analyst-insight-container span {
    background-color: transparent!important;
  }
`;
export const InsightsSectionRow = styled.div`
  display: flex;
  margin-top: 32px;
`;


export const ReportListingV2Wrapper = styled.div`
  ${({ theme }) => `
    margin-top: ${theme.spacing(3.5)};
  `}
`;

export const MoreInfo = styled.div`
  display: flex;
  align-items: center;
  & svg {
    margin-left: ${({ theme }) => theme.spacing(1)};
  }
  &:last-of-type {
    padding-left: 0;
  }
  &:first-of-type {
    padding-right: 0;
  }
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    &::after {
      content: '';
      display: block;
      height: 14px;
      margin: 0 ${theme.spacing(2)};
      width: 1px;
      background: ${theme.colors.neutrals.grey6};
    }
    &:last-child::after {
      display: none;
    }
  `}
`;

export const MoreText = styled(Text)`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

export const AmenitiesWrapper = styled.div`
  ${({ theme }) => `
    margin-top: ${theme.spacing(2.5)};
  `}
`;

export const MobileMapWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;
const DESKTOP_PADDING = 120;
const DESKTOP_GAP = 32;
const CONTACT_FORM_SIZE = 360;
export const BannerWrapper = styled.div`
  margin: 0 auto;
  width: calc(100% - ${CONTACT_FORM_SIZE}px - ${DESKTOP_PADDING}px - ${DESKTOP_GAP}px);
`;

export const BannerSection = styled.div`
  margin: 0 auto;
  max-width: 1440px;
`;

export const GalleryWrapper = styled.div(() => ({
  position: 'relative',
}));
